import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { env } from "../env";
import { getLocalStorage } from "../utils/function/localStorage";

export const socialApi = createApi({
  reducerPath: "socialApi",
  baseQuery: fetchBaseQuery({
    baseUrl: env.BASE_URL,
    prepareHeaders: (headers) => {
      headers.set("X-Auth-Token", getLocalStorage("tp_Aa_uth_access"));
      return headers;
    },
  }),
  tagTypes: [
    "SocialPost",
    "MyPosts",
    "Post",
    "PostStats",
    "LikedPosts",
    "CommentedPosts",
    "UserProfile",
    "FollowList",
    "EarningStats",
    "PromotionPosts",
    "PostSuggestions", // Added from App
    "PostShare",   
    "Categories",
    "PromotionThread"
  ],
  endpoints: (builder) => ({

    // ================= POSTS (V1) =================

    createSocialPost: builder.mutation({
      query: (formData) => ({
        url: "/secure/create-post",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["SocialPost", "MyPosts", 'PostStats'],
    }),

    getSocialPosts: builder.query({
      query: ({ page = 1, limit = 20, username }) => {
        let url = `/secure/posts?page=${page}&limit=${limit}`;
        if (username) url += `&username=${username}`;
        return url;
      },
      providesTags: ["SocialPost"],
    }),

    // ================= POSTS (V2 - Cursor Pagination) =================

    getSocialPostsV2: builder.query({
      query: ({ cursor, limit = 20 }) => ({
        url: `/secure/postsV2`,
        params: {
          ...(cursor ? { cursor } : {}),
          limit,
        },
        method: 'GET',
      }),
    }),

    // ================= MY POSTS =================

    getMyPosts: builder.query({
      query: ({ page = 1, limit = 10 } = {}) => ({
        url: `/secure/my-posts?page=${page}&limit=${limit}`,
        method: "POST",
      }),
      providesTags: ["MyPosts"],
    }),

    getMyPostsV2: builder.query({
      query: ({ cursor = null, limit = 20 }) => ({
        url: `/secure/myPostsV2`,
        params: {
          ...(cursor ? { cursor } : {}),
          limit,
        },
        method: 'GET',
      }),
    }),

    requestStandardEdit: builder.mutation({
      query: ({ postId, description }) => ({
        url: `/secure/standard/edit/${postId}`, // Matches App Endpoint
        method: 'PUT',
        body: { description }, // Matches App JSON body
      }),
      invalidatesTags: ['SocialPost', 'MyPosts', 'Post', 'PromotionPosts'],
    }),

    requestPromotionEdit: builder.mutation({
      query: ({ postId, ...body }) => ({
        url: `/secure/promote/edit/${postId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['SocialPost', 'MyPosts', 'Post', 'PromotionPosts'],
    }),

    // ================= PROMOTIONS =================

    getPromotionPosts: builder.query({
      query: ({ page = 1, limit = 20 }) =>
        `/secure/promotion-posts?page=${page}&limit=${limit}`,
      providesTags: ["PromotionPosts"],
    }),

    getPromotionPostsV2: builder.query({
      query: ({ cursor = null, limit = 20 }) => ({
        url: `/secure/promotion-postsV2`,
        params: {
          ...(cursor ? { cursor } : {}),
          limit,
        },
        method: 'GET',
      }),
    }),

    getMyPromotionsV2: builder.query({
      query: ({ username, cursor = null, limit = 20 }) => ({
        url: `/secure/getMyPromotionsV2/${username}`,
        method: 'GET',
        params: {
          ...(cursor ? { cursor } : {}),
          limit
        }
      }),
      providesTags: ["PromotionPosts"],
    }),

    // ================= PROFILE POSTS =================

    getProfilePosts: builder.query({
      query: ({ username, page = 1, limit = 10 }) =>
        `/profile/${username}/posts?page=${page}&limit=${limit}`,
      providesTags: ["Post"],
    }),

    getProfilePostsV2: builder.query({
      query: ({ username, cursor = null, limit = 20 }) => ({
        url: `/secure/profile-postsV2/${username}`,
        params: {
          ...(cursor ? { cursor } : {}),
          limit,
        },
        method: 'GET',
      }),
    }),

    // ================= LIKES & COMMENTS =================

    getLikedPosts: builder.query({
      query: ({ page = 1, limit = 10 } = {}) => ({
        url: `/secure/my-liked-posts?page=${page}&limit=${limit}`,
        method: "POST",
      }),
      providesTags: ["LikedPosts"],
    }),

    getLikedPostsV2: builder.query({
      query: ({ username, cursor = null, limit = 20 }) => ({
        url: `/secure/profile-likedPostsV2/${username}`,
        params: {
          ...(cursor ? { cursor } : {}),
          limit,
        },
        method: 'GET',
      }),
    }),

    getCommentedPosts: builder.query({
      query: ({ page = 1, limit = 10 } = {}) => ({
        url: `/secure/my-commented-posts?page=${page}&limit=${limit}`,
        method: "POST",
      }),
      providesTags: ["CommentedPosts"],
    }),

    getCommentedPostsV2: builder.query({
      query: ({ username, cursor = null, limit = 20 }) => ({
        url: `/secure/profile-commentedPostsV2/${username}`,
        params: {
          ...(cursor ? { cursor } : {}),
          limit,
        },
        method: 'GET',
      }),
    }),

    checkAndMarkMilestone: builder.mutation({
      query: () => ({
        url: '/secure/check-milestone',
        method: 'POST',
      }),
      // Invalidate UserProfile so we can see updated flags if needed
      invalidatesTags: ['UserProfile'],
    }),
    
    getMilestoneRewards: builder.query({
      query: () => ({
        url: '/secure/milestone-rewards',
        method: 'GET',
      }),
      providesTags: ['MilestoneRewards'],
      // Cache for 5 minutes since this data rarely changes
      keepUnusedDataFor: 300,
    }),
    

    toggleLikePost: builder.mutation({
      query: ({ postID }) => ({
        url: `/secure/${postID}/like`,
        method: "POST",
      }),
    }),

    addComment: builder.mutation({
      query: ({ postID, commentText }) => ({
        url: `/secure/${postID}/comment`,
        method: "POST",
        body: { commentText },
      }),
    }),

    getMostLikedPosts: builder.query({
      // We accept 'cursor' here to match the frontend logic
      query: ({ cursor = 1, limit = 20, username = null }) => {
        let url = `/secure/get-most-liked-posts?cursor=${cursor}&limit=${limit}`;
        if (username) {
          url += `&username=${username}`;
        }
        return { url: url, method: 'GET' };
      },
      providesTags: ['SocialPost'],
    }),

    getMostCommentedPosts: builder.query({
      query: ({ cursor = 1, limit = 20, username = null }) => {
        let url = `/secure/get-most-commented-posts?cursor=${cursor}&limit=${limit}`;
        if (username) {
          url += `&username=${username}`;
        }
        return { url: url, method: 'GET' };
      },
      providesTags: ['SocialPost'],
    }),
    getMostSharedPosts: builder.query({
      query: ({ cursor = 1, limit = 20, username = null }) => {
        let url = `/secure/get-most-shared-posts?cursor=${cursor}&limit=${limit}`;
        if (username) {
          url += `&username=${username}`;
        }
        return { url: url, method: 'GET' };
      },
      providesTags: ['SocialPost'],
    }),

    // ================= INDIVIDUAL POST =================

    getPostById: builder.query({
      query: ({ postId, username }) => {
        let url = `/secure/posts/${postId}`;
        if (username) url += `?username=${username}`;
        return url;
      },
      providesTags: ["Post"],
    }),

    incrementShareCount: builder.mutation({
      query: ({ postID }) => ({
        url: '/secure/posts/increment-share',
        method: 'POST',
        body: { postID },
      }),
      invalidatesTags: ['PostShare'],
    }),


    // ================= POLL =================

    votePoll: builder.mutation({
      query: ({ postID, optionId }) => ({
        url: "/secure/vote_poll",
        method: "POST",
        body: { postID, optionId },
      }),
      invalidatesTags: ["SocialPost", "MyPosts"],
    }),

    // ================= USER PROFILE =================

    getMyUserProfile: builder.query({
      query: () => "/secure/getmyuserprofile",
      providesTags: ["UserProfile"],
    }),

    getPublicUserProfile: builder.query({
      query: (username) =>
        `/secure/public-user-profile?username=${username}`,
      providesTags: ["UserProfile"],
    }),

    updateProfileImage: builder.mutation({
      query: (formData) => ({
        url: "/secure/update_profile_image",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["UserProfile"],
    }),

    updateCoverImage: builder.mutation({
      query: (formData) => ({
        url: "/secure/update_cover_image",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["UserProfile"],
    }),

    // ================= FOLLOW =================

    followUser: builder.mutation({
      query: ({ targetUsername }) => ({
        url: "/secure/follow-user",
        method: "POST",
        body: { username: targetUsername },
      }),
      invalidatesTags: ["UserProfile", "FollowList"],
    }),

    unfollowUser: builder.mutation({
      query: ({ targetUsername }) => ({
        url: "/secure/unfollow-user",
        method: "POST",
        body: { username: targetUsername },
      }),
      invalidatesTags: ["UserProfile", "FollowList"],
    }),

    getFollowList: builder.query({
      query: ({ username, type }) =>
        `/secure/follow-list?username=${username}&type=${type}`,
      providesTags: ["FollowList"],
    }),

    // ================= EARNINGS =================

    getUserEarningStats: builder.query({
      query: (username) =>
        `/secure/user-earning-stats?username=${username}`,
      providesTags: ["EarningStats"],
    }),

    // ================= SEARCH & SUGGESTIONS =================

    searchSocialUsers: builder.query({
      query: (searchTerm) =>
        `/secure/search-social-users?searchTerm=${encodeURIComponent(searchTerm)}`,
    }),

    searchSocialPosts: builder.query({
      query: ({ query, type = 'promotions', page = 1, limit = 20 }) => ({
        url: '/secure/posts/search',
        method: 'POST',
        body: { query, type, page, limit }
      })
    }),

    getSuggestedUsers: builder.query({
      query: () => ({
        url: '/secure/suggested-users',
        method: 'GET',
      }),
    }),
    // Add these to your mutations/queries
getPromotionTaskConfig: builder.query({
      query: () => ({
        url: '/secure/get-promotion-task-config', // Ensure this matches your backend route
        method: 'GET',
      }),
    }),

    // 2. The Mutation Endpoint
    createTaskFromPromotion: builder.mutation({
      query: (data) => ({
        url: '/secure/create-task-from-promotion',
        method: 'POST',
        body: data,
      }),
    }),

    getFollowingPosts: builder.query({
      query: ({ cursor = null, limit = 20 }) => ({
        url: `/secure/get-users-following-post`,
        params: {
          ...(cursor ? { cursor } : {}),
          limit,
        },
        method: 'GET',
      }),
      providesTags: ["SocialPost"],
    }),

    getPromotionThread: builder.query({
      query: (postId) => ({
        url: `/secure/promotion-thread/${postId}`,
        method: 'GET',
      }),
      providesTags: ['PromotionThread'],
    }),
    getPromotionCategories: builder.query({
      query: () => ({
        url: "/secure/promote/categories",
        method: "GET",
      }),
      providesTags: ["Categories"],
    }),
    getPromotionsByCategory: builder.query({
      query: ({ category, cursor = null, limit = 20 }) => ({
        url: "/secure/promotions/category",
        method: "GET",
        params: { category, cursor, limit }
      }),
      providesTags: ["PromotionPosts"],
    }),

    reportPost: builder.mutation({
      query: ({ postID, category }) => ({
        url: '/secure/report-post',
        method: 'POST',
        body: { postID, category },
      }),
    }),

  }),


});

export const {
  // Mutations
  useCreateSocialPostMutation,
  useToggleLikePostMutation,
  useAddCommentMutation,
  useVotePollMutation,
  useFollowUserMutation,
  useUnfollowUserMutation,
  useUpdateProfileImageMutation,
  useUpdateCoverImageMutation,
  useIncrementShareCountMutation,

useLazyGetPromotionTaskConfigQuery, 
  useCreateTaskFromPromotionMutation,

  // Queries (V1 Standard)
  useGetSocialPostsQuery,
  useGetPromotionPostsQuery,
  useGetPostByIdQuery,
  useGetMyPostsQuery,
  useGetProfilePostsQuery,
  useGetLikedPostsQuery,
  useGetCommentedPostsQuery,
  useGetMyUserProfileQuery,
  useGetPublicUserProfileQuery,
  useGetFollowListQuery,
  useGetUserEarningStatsQuery,
  useSearchSocialUsersQuery,
  useSearchSocialPostsQuery,
  useGetSuggestedUsersQuery,
useLazyGetPublicUserProfileQuery,
  // Queries (V2 & Lazy for Pagination)
  useGetSocialPostsV2Query,
  useLazyGetSocialPostsV2Query,

  useGetPromotionPostsV2Query,
  useLazyGetPromotionPostsV2Query,

  useGetMyPostsV2Query,
  useLazyGetMyPostsV2Query,

  useGetProfilePostsV2Query,
  useLazyGetProfilePostsV2Query,

  useGetLikedPostsV2Query,
  useLazyGetLikedPostsV2Query,

  useGetCommentedPostsV2Query,
  useLazyGetCommentedPostsV2Query,

  useGetMyPromotionsV2Query,
  useLazyGetMyPromotionsV2Query,

  useLazyGetPostByIdQuery,
  useLazyGetFollowListQuery,

  useLazyGetMostLikedPostsQuery,
  useLazyGetMostCommentedPostsQuery,
  useLazyGetMostSharedPostsQuery,
  useRequestStandardEditMutation,
  useRequestPromotionEditMutation,
  useReportPostMutation,
  useGetPromotionThreadQuery,
  useLazyGetPromotionThreadQuery,
  useGetPromotionCategoriesQuery,
  useGetPromotionsByCategoryQuery,
  useLazyGetPromotionsByCategoryQuery,
  useLazyGetFollowingPostsQuery,
    useCheckAndMarkMilestoneMutation,
  useGetMilestoneRewardsQuery,
  useLazySearchGlobalReferralsQuery
} = socialApi;